/**
 * OAuth 回调页 — 2 frame share component(GmWWr 成功 + zPcq8 失败)。
 *
 * frame_id 动态:`data-testid={status === 'success' ? 'GmWWr' : 'zPcq8'}`
 *   (per Phase 4-A multi-frame share component 模式;参 share-pack / recharge-callback)
 *
 * 路径:`/oauth/callback?provider=google&code=xxx&state=xxx`(成功)
 *      `/oauth/callback?error=access_denied&error_description=...&trace_id=...`(失败)
 * 守卫:🌍 Public(回调时未登录;exchange 后才注入 session)
 *
 * 逻辑:
 *  1. 读 ?error → 含 error → 直显 zPcq8 失败态(不调 exchange)
 *  2. 读 ?code → 含 code → 调 supabase.auth.exchangeCodeForSession(window.location.href)
 *  3. exchange 成功 → 显 GmWWr 成功态 + 1.5s 后 navigate('/')(home page LNMX9)
 *  4. exchange 失败 → 显 zPcq8 失败态 + error.message
 *  5. 无 code 也无 error → 显 zPcq8 + 'OAUTH_NO_CODE'
 *
 * Backend:0 新(supabase managed OAuth flow;若需要后端 link 关联走既有
 *   `POST /gapi/public/auth/oauth/callback` per auth.md §3 — MVP 阶段前端直调 exchange 即可)。
 *
 * Spec:docs/superpowers/specs/2026-05-26-batch-d-user-routes-design.md §4.2
 */
import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { getSupabase } from '@dogdisk/shared'
import { MobileScreen, TopBar } from '../components/mobile-screen'

type Status = 'loading' | 'success' | 'fail'

export function OAuthCallbackPage() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const [status, setStatus] = useState<Status>('loading')
  const [errorCode, setErrorCode] = useState<string | null>(null)
  const [errorDesc, setErrorDesc] = useState<string | null>(null)
  const [traceId, setTraceId] = useState<string | null>(null)

  useEffect(() => {
    const errorParam = params.get('error')
    const codeParam = params.get('code')
    const traceParam = params.get('trace_id')
    setTraceId(traceParam)

    // case 1: URL 含 ?error → 直显失败态(不调 exchange)
    if (errorParam) {
      setStatus('fail')
      setErrorCode(errorParam)
      setErrorDesc(params.get('error_description') ?? errorParam)
      return
    }

    // case 2: 无 code 也无 error → 失败态(OAUTH_NO_CODE)
    if (!codeParam) {
      setStatus('fail')
      setErrorCode('OAUTH_NO_CODE')
      setErrorDesc('回调 URL 缺少 code 参数,请重新发起 OAuth 登录')
      return
    }

    // case 3: 调 supabase.auth.exchangeCodeForSession
    let cancelled = false
    void (async () => {
      try {
        const sb = getSupabase()
        const { error } = await sb.auth.exchangeCodeForSession(window.location.href)
        if (cancelled) return
        if (error) {
          setStatus('fail')
          setErrorCode(error.name || 'OAUTH_EXCHANGE_FAILED')
          setErrorDesc(error.message)
          return
        }
        setStatus('success')
        // 1.5s 后跳首页(LNMX9 `/`;spec §4.2 称 /home,项目实际为 /)
        setTimeout(() => {
          if (!cancelled) navigate('/')
        }, 1_500)
      } catch (err) {
        if (cancelled) return
        setStatus('fail')
        setErrorCode('OAUTH_EXCHANGE_ERROR')
        setErrorDesc(err instanceof Error ? err.message : '验证失败,请重试')
      }
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // 动态 testid:per Phase 4-A multi-frame share component 模式
  const frameTestId = status === 'success' ? 'GmWWr' : status === 'fail' ? 'zPcq8' : 'loading'

  return (
    <MobileScreen>
      <TopBar title="账号绑定" hideBack />

      {status === 'loading' && (
        <div
          data-testid="loading"
          className="flex flex-1 flex-col items-center justify-center gap-4 px-6 py-12 text-center"
        >
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-brand-200 border-t-brand-500" />
          <p className="text-[15px] text-ink">正在验证身份…</p>
        </div>
      )}

      {status === 'success' && (
        <div
          data-testid={frameTestId}
          className="flex flex-1 flex-col items-center justify-center gap-4 px-6 py-12 text-center"
        >
          <div
            data-testid={`${frameTestId}-icon`}
            className="flex h-24 w-24 items-center justify-center rounded-full bg-[#ebfaf6] text-[#19caad]"
          >
            <svg
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M5 12l5 5L20 7" />
            </svg>
          </div>
          <p className="text-[18px] font-semibold text-ink">已通过验证</p>
          <p className="text-[13px] text-ink-mute">3 秒后自动跳转到首页...</p>
        </div>
      )}

      {status === 'fail' && (
        <div
          data-testid={frameTestId}
          className="flex flex-1 flex-col items-center justify-center gap-4 px-6 py-12 text-center"
        >
          <div
            data-testid={`${frameTestId}-icon`}
            className="flex h-24 w-24 items-center justify-center rounded-full bg-[#FEE2E2] text-[#FF6B6B]"
          >
            <svg
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="6" y1="6" x2="18" y2="18" />
              <line x1="6" y1="18" x2="18" y2="6" />
            </svg>
          </div>
          <p className="text-[18px] font-semibold text-ink">登录失败</p>
          <p className="text-[13px] text-ink-mute">OAuth 提供商拒绝了请求,或会话已过期。</p>

          <div
            data-testid={`${frameTestId}-error-info`}
            className="mt-2 flex flex-col gap-1 self-stretch rounded-lg bg-[#FFFBEB] px-4 py-3 text-left text-[12px] text-[#92400E]"
          >
            {errorCode && (
              <p>
                错误码:<span className="font-mono">{errorCode}</span>
              </p>
            )}
            {errorDesc && errorDesc !== errorCode && <p className="text-[11px]">{errorDesc}</p>}
            {traceId && (
              <p data-testid={`${frameTestId}-trace-id`}>
                trace_id:<span className="font-mono">{traceId}</span>
              </p>
            )}
          </div>

          <div className="flex w-full max-w-[300px] flex-col gap-2 pt-4">
            <button
              type="button"
              data-testid={`${frameTestId}-back-login`}
              onClick={() => navigate('/auth/login')}
              className="h-11 rounded-lg bg-brand-500 text-[15px] font-semibold text-white active:bg-brand-600"
            >
              返回登录
            </button>
          </div>
        </div>
      )}
    </MobileScreen>
  )
}

export default OAuthCallbackPage
